"use client";

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import type { TreeNode } from "@/lib/taxonomy";
import {
  getNodeCheckState,
  toggleNode,
  countSelectedLeaves,
  getAllLeafIds,
} from "@/lib/taxonomy";

// ═══ TREE FILTER ═══

function filterTree(tree: TreeNode[], query: string): TreeNode[] {
  const q = query.toLowerCase().trim();
  if (!q) return tree;

  function matchNode(node: TreeNode): TreeNode | null {
    const labelMatch = node.label.toLowerCase().includes(q);

    if (!node.children || node.children.length === 0) {
      return labelMatch ? node : null;
    }

    // Parent matches: return with all children
    if (labelMatch) return node;

    // Check children recursively
    const matched = node.children
      .map((c) => matchNode(c))
      .filter((c): c is TreeNode => c !== null);

    return matched.length > 0 ? { ...node, children: matched } : null;
  }

  return tree
    .map((n) => matchNode(n))
    .filter((n): n is TreeNode => n !== null);
}

// ═══ TRI-STATE CHECKBOX ═══

function TreeCheckbox({
  state,
  onClick,
  id,
}: {
  state: "checked" | "unchecked" | "indeterminate";
  onClick: () => void;
  id: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === "indeterminate";
    }
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      id={id}
      checked={state === "checked"}
      onChange={onClick}
      className="w-5 h-5 rounded accent-gold cursor-pointer shrink-0"
      aria-checked={state === "indeterminate" ? "mixed" : state === "checked"}
    />
  );
}

// ═══ TREE NODE ROW ═══

function TreeNodeRow({
  node,
  selected,
  onToggle,
  depth,
  defaultExpanded,
  forceExpanded = false,
}: {
  node: TreeNode;
  selected: Set<string>;
  onToggle: (node: TreeNode) => void;
  depth: number;
  defaultExpanded: boolean;
  forceExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isExpanded = forceExpanded || expanded;
  const hasChildren = node.children && node.children.length > 0;
  const state = getNodeCheckState(node, selected);
  const selectedCount = hasChildren ? countSelectedLeaves(node, selected) : 0;
  const totalLeaves = hasChildren ? getAllLeafIds(node).length : 0;
  const checkboxId = `tree-${node.id}`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" && hasChildren && !isExpanded) {
      e.preventDefault();
      setExpanded(true);
    } else if (e.key === "ArrowLeft" && hasChildren && isExpanded) {
      e.preventDefault();
      setExpanded(false);
    } else if (e.key === " ") {
      e.preventDefault();
      onToggle(node);
    }
  };

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={state === "checked"}
    >
      <div
        className={`flex items-center gap-2 py-2.5 rounded-sm transition-colors hover:bg-navy/[0.03] ${
          depth === 0 ? "px-1" : depth === 1 ? "pl-5 pr-1" : "pl-7 sm:pl-9 pr-1"
        }`}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-5 h-5 flex items-center justify-center rounded shrink-0 text-text-muted hover:text-navy hover:bg-navy/[0.06] transition-all bg-transparent border-none cursor-pointer p-0"
            tabIndex={-1}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className={`transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
            >
              <path
                d="M4.5 3L7.5 6L4.5 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        {/* Checkbox */}
        <TreeCheckbox
          state={state}
          onClick={() => onToggle(node)}
          id={checkboxId}
        />

        {/* Label */}
        <label
          htmlFor={checkboxId}
          className={`cursor-pointer select-none flex items-center gap-1.5 flex-1 min-w-0 ${
            depth === 0
              ? "text-[13px] font-semibold text-charcoal"
              : depth === 1
                ? "text-[13px] font-medium text-text-body"
                : "text-[12px] font-normal text-text-body"
          }`}
        >
          {node.emoji && <span className="shrink-0">{node.emoji}</span>}
          <span className="truncate">{node.label}</span>
        </label>

        {/* Selected count badge (collapsed parents only) */}
        {hasChildren && !isExpanded && selectedCount > 0 && (
          <span className="text-[10px] font-semibold text-gold bg-gold/10 px-1.5 py-0.5 rounded-full shrink-0">
            {selectedCount === totalLeaves ? "All" : selectedCount}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && (
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          }`}
          role="group"
        >
          {node.children!.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              selected={selected}
              onToggle={onToggle}
              depth={depth + 1}
              defaultExpanded={false}
              forceExpanded={forceExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ SEARCH INPUT ═══

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-text-muted"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ═══ CASCADING TREE ═══

export function CascadingTree({
  tree,
  selected,
  onChange,
  label,
  defaultTopLevelExpanded = false,
  maxHeight,
  searchable = false,
  maxLeaves,
  maxLeavesMessage,
  otherTree,
  otherLabel = "All other roles",
}: {
  tree: TreeNode[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  label?: string;
  defaultTopLevelExpanded?: boolean;
  maxHeight?: string;
  searchable?: boolean;
  maxLeaves?: number;
  maxLeavesMessage?: string;
  /** Secondary tree shown in a collapsible "other" section below the main tree */
  otherTree?: TreeNode[];
  /** Label for the collapsible other section */
  otherLabel?: string;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [otherExpanded, setOtherExpanded] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Combine both trees for total count and search
  const combinedTree = useMemo(
    () => (otherTree ? [...tree, ...otherTree] : tree),
    [tree, otherTree],
  );

  // Reset search when the tree identity changes (e.g. switching season in signup)
  const treeKey = combinedTree.map((n) => n.id).join(",");
  const [prevTreeKey, setPrevTreeKey] = useState(treeKey);
  if (prevTreeKey !== treeKey) {
    setPrevTreeKey(treeKey);
    setSearchQuery("");
  }

  const isFiltering = searchQuery.trim().length > 0;
  const filteredTree = useMemo(
    () => (isFiltering ? filterTree(tree, searchQuery) : tree),
    [tree, searchQuery, isFiltering],
  );
  const filteredOtherTree = useMemo(
    () =>
      otherTree && isFiltering
        ? filterTree(otherTree, searchQuery)
        : otherTree,
    [otherTree, searchQuery, isFiltering],
  );

  const handleToggle = useCallback(
    (node: TreeNode) => {
      const next = toggleNode(node, selected);
      if (maxLeaves != null && next.size > maxLeaves) return;
      onChange(next);
    },
    [selected, onChange, maxLeaves],
  );

  const totalSelected = combinedTree.reduce(
    (sum, node) => sum + countSelectedLeaves(node, selected),
    0,
  );

  const otherSelectedCount = otherTree
    ? otherTree.reduce((sum, node) => sum + countSelectedLeaves(node, selected), 0)
    : 0;

  const hasNoResults =
    filteredTree.length === 0 &&
    (!filteredOtherTree || filteredOtherTree.length === 0);

  return (
    <div>
      {label && (
        <div className="flex justify-between items-center mb-2">
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px] block">
            {label}
          </label>
          {totalSelected > 0 && (
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="text-[10px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans"
            >
              Clear ({totalSelected})
            </button>
          )}
        </div>
      )}

      {maxLeaves != null && totalSelected >= maxLeaves && (
        <p className="text-[12px] text-gold mb-2 mt-0">
          {maxLeavesMessage ?? `You can select up to ${maxLeaves}.`}
        </p>
      )}

      {searchable && (
        <div className="relative mb-2">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-9 py-2.5 text-[13px] border border-border-light rounded-[8px] bg-white placeholder:text-text-muted/60 focus:outline-none focus:border-gold/50 transition-colors min-h-[44px]"
            aria-label={`Search ${label ?? "options"}`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                searchRef.current?.focus();
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-text-muted hover:text-navy hover:bg-navy/[0.06] transition-colors bg-transparent border-none cursor-pointer p-0"
              aria-label="Clear search"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      )}

      <div
        className={`border border-border-light rounded-[10px] bg-warm-bg/50 p-2 ${
          maxHeight ? "overflow-y-auto" : ""
        }`}
        style={maxHeight ? { maxHeight } : undefined}
        role="tree"
      >
        {/* Main (suggested) tree */}
        {filteredTree.length > 0 ? (
          filteredTree.map((node) => (
            <TreeNodeRow
              key={node.id}
              node={node}
              selected={selected}
              onToggle={handleToggle}
              depth={0}
              defaultExpanded={defaultTopLevelExpanded}
              forceExpanded={isFiltering}
            />
          ))
        ) : !otherTree ? (
          <p className="text-text-muted text-[13px] text-center py-4">
            No results for &ldquo;{searchQuery}&rdquo;
          </p>
        ) : null}

        {/* Other roles section (collapsible, only when otherTree is provided) */}
        {filteredOtherTree && filteredOtherTree.length > 0 && (
          <>
            {filteredTree.length > 0 && !isFiltering && (
              <div className="border-t border-border-light my-2" />
            )}
            {!isFiltering ? (
              <div>
                <button
                  type="button"
                  onClick={() => setOtherExpanded(!otherExpanded)}
                  className="w-full flex items-center gap-2 py-2.5 px-1 rounded-sm transition-colors hover:bg-navy/[0.03] bg-transparent border-none cursor-pointer text-left"
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 text-text-muted">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className={`transition-transform duration-200 ${otherExpanded ? "rotate-90" : ""}`}
                    >
                      <path
                        d="M4.5 3L7.5 6L4.5 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="text-[12px] font-medium text-text-muted">
                    {otherLabel}
                  </span>
                  {otherSelectedCount > 0 && (
                    <span className="text-[10px] font-semibold text-gold bg-gold/10 px-1.5 py-0.5 rounded-full shrink-0 ml-auto">
                      {otherSelectedCount}
                    </span>
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    otherExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  {filteredOtherTree.map((node) => (
                    <TreeNodeRow
                      key={node.id}
                      node={node}
                      selected={selected}
                      onToggle={handleToggle}
                      depth={0}
                      defaultExpanded={false}
                      forceExpanded={false}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* When searching, show other results inline */
              filteredOtherTree.map((node) => (
                <TreeNodeRow
                  key={node.id}
                  node={node}
                  selected={selected}
                  onToggle={handleToggle}
                  depth={0}
                  defaultExpanded={false}
                  forceExpanded={isFiltering}
                />
              ))
            )}
          </>
        )}

        {/* No results across both trees */}
        {hasNoResults && isFiltering && (
          <p className="text-text-muted text-[13px] text-center py-4">
            No results for &ldquo;{searchQuery}&rdquo;
          </p>
        )}
      </div>
    </div>
  );
}
